import React, { FunctionComponent, useEffect, useMemo, useRef } from "react";
import { combineLatest } from "rxjs";
import * as d3 from "d3";
import { filter } from "rxjs/operators";
import { styled } from "@iventis/styles";
import { fontSizes } from "@iventis/styles/src/atomic-rules";
import debounce from "lodash.debounce";
import { useGroupAxisHeight, usePropsStream, useUpdateTimeScale, useViewportStream, useZoomStream } from "./hooks";
import { ContextPropsData, TIME_AXIS_WIDTH, ViewportSizeData } from "./calendar-context";
import { distinctUntilPropsChange } from "./distinct-keys-operator";

const TICK_SPACING = 60;

const getNumberOfTicks = (availableHeight: number): number => Math.floor(availableHeight / TICK_SPACING);

const TimeAxisUnMemoised: FunctionComponent<{ onTimeChange?(update: { start: Date; end: Date }): void }> = ({ onTimeChange: _onTimeChange }) => {
    const timeGridRef = useRef();
    const timeAxisRef = useRef();

    const scale = useMemo(() => d3.scaleTime().domain([]).nice(), []);
    const axis = useMemo(() => d3.axisLeft(scale).tickSizeInner(TIME_AXIS_WIDTH).tickSizeOuter(TIME_AXIS_WIDTH).tickFormat(d3.timeFormat("%H:%M")), [scale]);
    const gridLinesAxis = useMemo(() => d3.axisLeft(scale).tickFormat(() => ""), [scale]);

    const onTimeChange = debounce(_onTimeChange, 500);

    const props$ = usePropsStream();
    const viewport$ = useViewportStream();
    const zoom$ = useZoomStream();
    const groupAxisHeight$ = useGroupAxisHeight();
    const updateTimeScale = useUpdateTimeScale();
    // TODO: optimize event subscription
    useEffect(() => {
        const sub = combineLatest([
            props$.pipe(filter<ContextPropsData>(Boolean), distinctUntilPropsChange(["start", "end"])),
            viewport$.pipe(filter<ViewportSizeData>(Boolean)),
            zoom$,
            groupAxisHeight$,
        ]).subscribe(([props, viewport, zoomEvent, groupAxisHeight]) => {
            const { start, end } = props;
            const { height, width } = viewport;
            scale.domain([start, end]);
            scale.range([groupAxisHeight, height]);

            // We need to ensure there is a valid zoom event before we rescale
            if (zoomEvent) {
                const zoomedScale = zoomEvent.transform.rescaleY(scale);
                scale.domain(zoomedScale.domain());
            }
            onTimeChange?.({ start: scale.invert(scale.range()[0]), end: scale.invert(scale.range()[1]) });
            updateTimeScale(scale);

            axis.ticks(getNumberOfTicks(height - groupAxisHeight));
            gridLinesAxis.tickSizeInner(-width);
            gridLinesAxis.ticks(getNumberOfTicks(height - groupAxisHeight));

            d3.select<SVGGElement, unknown>(timeAxisRef.current)
                .call(axis)
                .attr("transform", `translate(${TIME_AXIS_WIDTH}, 0)`)
                .selectAll("g.tick text")
                .attr("x", -5)
                .attr("y", 10);

            d3.select<SVGGElement, unknown>(timeGridRef.current).call(gridLinesAxis).attr("transform", `translate(${TIME_AXIS_WIDTH}, 0)`);
        });

        return () => sub.unsubscribe();
    }, [props$, viewport$, zoom$, groupAxisHeight$, scale, axis, gridLinesAxis, updateTimeScale]);

    return (
        <g className="timeAxis">
            <TimeAxisElement ref={timeAxisRef} />
            <TimeAxisGridLines ref={timeGridRef} />
        </g>
    );
};

const TimeAxisGridLines = styled.g`
    line {
        stroke: #ccc;
    }

    .domain {
        stroke-width: 0;
    }
`;

const TimeAxisElement = styled.g`
    .domain {
        stroke-width: 0;
    }
    g.tick text {
        font-size: ${fontSizes.small};
        font-weight: 600;
        color: #797979;
    }
    line {
        stroke: #ccc;
    }
`;

export const TimeAxis = React.memo(TimeAxisUnMemoised);
